import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

function CategoryManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const snapshot = await getDocs(categoriesRef);
      const categoriesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      const newCategory = {
        name: 'New Category',
        description: '',
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'categories'), newCategory);
      const categoryWithId = { ...newCategory, id: docRef.id };
      setCategories([categoryWithId, ...categories]);
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'categories', id));
      setCategories(categories.filter(category => category.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleStatusToggle = async (categoryId) => {
    try {
      const category = categories.find(t => t.id === categoryId);
      const newStatus = category.status === 'active' ? 'inactive' : 'active';
      
      await updateDoc(doc(db, 'categories', categoryId), { 
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      setCategories(categories.map(category =>
        category.id === categoryId
          ? { ...category, status: newStatus }
          : category
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleNameChange = async (categoryId, newName) => {
    try {
      await updateDoc(doc(db, 'categories', categoryId), { 
        name: newName,
        updatedAt: serverTimestamp()
      });
      
      setCategories(categories.map(category =>
        category.id === categoryId
          ? { ...category, name: newName }
          : category
      ));
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  const handleDescriptionChange = async (categoryId, newDescription) => {
    try {
      await updateDoc(doc(db, 'categories', categoryId), { 
        description: newDescription,
        updatedAt: serverTimestamp()
      });
      
      setCategories(categories.map(category =>
        category.id === categoryId
          ? { ...category, description: newDescription }
          : category
      ));
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  const handleNameEdit = (categoryId, isEditing) => {
    setCategories(categories.map(category =>
      category.id === categoryId
        ? { ...category, isEditing }
        : category
    ));
  };

  return (
    <div className="data-container">
      <button className="add-btn" onClick={handleAdd}>
        <FaPlus /> Add Type
      </button>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {categories.map(category => (
            <tr key={category.id}>
              <td>
                {category.isEditing ? (
                  <input
                    type="text"
                    value={category.name}
                    onChange={(e) => handleNameChange(category.id, e.target.value)}
                    onBlur={() => handleNameEdit(category.id, false)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleNameEdit(category.id, false);
                      }
                    }}
                    autoFocus
                    className="name-input"
                  />
                ) : (
                  <span 
                    onClick={() => handleNameEdit(category.id, true)}
                    className="editable-name"
                  >
                    {category.name}
                  </span>
                )}
              </td>
              <td>
                <input
                  type="text"
                  value={category.description || ''}
                  onChange={(e) => handleDescriptionChange(category.id, e.target.value)}
                  className="description-input"
                  placeholder="Enter description"
                />
              </td>
              <td>
                <button 
                  className={`status-badge ${category.status}`}
                  onClick={() => handleStatusToggle(category.id)}
                >
                  {category.status}
                </button>
              </td>
              <td>
                <button className="delete-button" onClick={() => handleDelete(category.id)}>
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CategoryManagement;
